import { Divider, Typography, Box, Card, Stack } from '@mui/material';

import AssistanceFooter from 'components/Common/AssistanceFooter';
import DetailItem from 'components/Staff/ItemDetail';
import Profile from 'components/Staff/Profile';
import { useNavigate } from 'react-router-dom';
import MenuList from 'components/Common/MenuList';
import routes from 'constants/routes';
import Button from 'components/Common/Button';
import { dateFormat, formatAmount } from 'utils/helpers';
import MuiLoader from 'components/Common/Loader';
import GeneratePDF from 'components/PDF/GeneratePDF';

interface ButtonProps {
    loading: boolean;
    onClick?: () => void;
    disabled: boolean;
    title: string;
}

interface TransactionsDetails {
    balance: number;
    confirmationCode: string;
    transactionDateTime: string;
    myRole?: string;
    username: string;
    logoUrl: string;
    description?: string;
    name: string;
    to?: string;
    m2MRequest?: boolean;
    status: string;
    canRequestRefund?: boolean;
}
interface TransDetailType {
    type?: string;
    details: TransactionsDetails;
    loading?: boolean;
    showButtons?: boolean;
    cancelButtonProps?: ButtonProps;
    submitButtonProps?: ButtonProps;
}

const commonStyles = { fontSize: 16, color: '#1F2937', fontWeight: 400 };

export default function TransactionDetails({ type, details, loading, showButtons, submitButtonProps, cancelButtonProps }: TransDetailType) {
    let navigate = useNavigate();
    const {
        balance,
        confirmationCode = '',
        transactionDateTime,
        username,
        logoUrl,
        description,
        status,
        myRole,
        name,
        to = '',
        // canRequestRefund, // See the logic below before removing this
    } = details || {};
    const receiptId = `transaction-${confirmationCode}`;
    //temporary servicefee
    const servicefee = 0;
    const totalAmount = servicefee + (balance || 0);

    const handleIssueRefund = () => {
        navigate(routes.community.issueRefund.replace(':id', confirmationCode));
    };

    return (
        <>
            <MuiLoader absolute loading={!!loading} />
            <Card sx={{ p: '1vh', mx: { xs: '1vh', lg: '20vh' }, my: 1 }}>
                {type === 'community' ? (
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column', // Change from 'row' to 'column' to stack items vertically
                            justifyContent: 'flex-end', // Align the content to the right
                            alignItems: 'flex-end', // Align the content to the top and right
                            marginTop: 2,
                        }}
                    >
                        <MenuList
                            mt={'1rem'}
                            menuItems={[
                                {
                                    item: 'Share Receipt',
                                    render: () => <GeneratePDF elementId={receiptId}>Share Receipt</GeneratePDF>,
                                },
                                {
                                    item: ' Issue Refund',
                                    onClick: handleIssueRefund,
                                    // TODO: uncomment the below line when canRequestRefund flag work as expected.
                                    // disabled: !canRequestRefund
                                },
                                // You can add more menu items with their onClick functions here
                            ]}
                        />
                    </div>
                ) : null}

                <Box
                    id={receiptId}
                    sx={{ paddingTop: '2rem', paddingLeft: { xs: '2vh', sm: '10vh' }, paddingRight: { xs: '2vh', sm: '10vh' } }}
                >
                    <Box
                        display="flex"
                        flexDirection={'column'}
                        justifyContent="space-between"
                        alignItems="flex-start"
                        sx={{ marginBottom: 2 }}
                    >
                        <Typography sx={commonStyles} variant="h1">
                            {type ? 'Transaction' : 'Invoice'} #: {confirmationCode}
                        </Typography>
                        <span style={{ fontSize: 14, color: '#1F2937', fontWeight: 300 }}>
                            Date : {dateFormat(transactionDateTime, 'MMMM DD, YYYY hh:mmA')}
                        </span>
                    </Box>
                    <Typography sx={{ ...commonStyles, mt: 6 }} variant="h1">
                        {type ? 'Sender Info' : myRole === 'SENDER' ? 'From' : 'To'}
                    </Typography>
                    <Profile width={'30rem'} user={{ name, username: username, logoUrl: logoUrl }} />
                    <Typography sx={{ ...commonStyles, mt: 6 }} variant="h1">
                        Invoice Description
                    </Typography>
                    <Typography sx={{ fontSize: 14, color: '#1F2937', fontWeight: 400, mt: 1 }} variant="h1">
                        {description || ''}
                    </Typography>
                    <Typography sx={{ ...commonStyles, mt: 6 }} variant="h1">
                        Transaction Details
                    </Typography>
                    <Divider sx={{ my: 1 }} />
                    <div style={{ marginBottom: '12vh' }}>
                        {type === 'community' ? (
                            <>
                                <DetailItem fontWeight={400} textAlign="right" labelWidth={'100%'} label="To" value={to} />
                                <DetailItem fontWeight={400} textAlign="right" labelWidth={'100%'} label="Status" value={status} />
                            </>
                        ) : null}
                        <DetailItem
                            fontWeight={400}
                            textAlign="right"
                            labelWidth={'100%'}
                            label="Service fee"
                            value={formatAmount(servicefee, 'cents')}
                        />
                        <DetailItem
                            fontWeight={400}
                            textAlign="right"
                            labelWidth={'100%'}
                            label="Amount"
                            value={formatAmount(balance, 'cents')}
                        />
                        <Divider sx={{ my: 1 }} />
                        <DetailItem
                            fontWeight={400}
                            textAlign="right"
                            labelWidth={'100%'}
                            label="Total Amount"
                            value={formatAmount(totalAmount, 'cents')}
                        />
                    </div>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }} gap={2}>
                        {showButtons && (
                            <Stack direction="row" width="100%" gap={2} justifyContent="sp" sx={{ marginBottom: '7vh' }}>
                                <Button width={'100%'} {...cancelButtonProps} />
                                <Button width={'100%'} {...submitButtonProps} />
                            </Stack>
                        )}
                    </Box>
                    <AssistanceFooter type="Issue with this Transaction?" />
                </Box>
            </Card>
        </>
    );
}
//
